import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CreditCard, ArrowLeft, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

export default function MockCheckoutPage({ token, setUser }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tier = searchParams.get('tier') || 'pro';
  const billing = searchParams.get('billing') || 'monthly';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');

  const plans = {
    starter: { name: 'Identity Starter', monthly: 4.99, annual: 39.99 },
    pro: { name: 'Align Pro', monthly: 9.99, annual: 79.99 },
    elite: { name: 'Elite Pioneer', monthly: 24.99, annual: 199.99 },
  };

  const selectedPlan = plans[tier] || plans.pro;
  const price = billing === 'monthly' ? selectedPlan.monthly : selectedPlan.annual;

  const handlePayment = async (simulateSuccess) => {
    setLoading(true);
    setError('');

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    if (!simulateSuccess) {
      setError('Your card was declined. Please try another card or check details.');
      setLoading(false);
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    try {
      const response = await fetch(`${backendUrl}/api/payment/mock-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tier,
          billing_period: billing,
        }),
      });

      if (!response.ok) {
        throw new Error('Payment verification failed. Please try again.');
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      setSuccess(true);
      
      // Keep success screen for 1.5 seconds, then route to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Something went wrong processing your subscription.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] dark:bg-[#121212] flex items-center justify-center p-4 transition-colors duration-200">
      <div className="w-full max-w-md bg-card border border-border rounded-3xl shadow-xl overflow-hidden dark:bg-zinc-950 bg-white">
        
        {/* Success Screen Overlay */}
        {success && (
          <div className="p-8 text-center flex flex-col items-center justify-center min-h-[450px] animate-fade-in">
            <CheckCircle className="h-16 w-16 text-emerald-500 mb-4 animate-bounce" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground text-sm max-w-xs mb-4">
              Your subscription to {selectedPlan.name} is now active. Unlocking your dashboard...
            </p>
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        )}

        {!success && (
          <>
            <div className="p-6 border-b border-border flex items-center gap-3">
              <button
                onClick={() => navigate('/checkout')}
                className="p-2 hover:bg-neutral-100 dark:hover:bg-zinc-900 rounded-full transition"
              >
                <ArrowLeft className="h-5 w-5 text-foreground" />
              </button>
              <div>
                <h2 className="text-xl font-bold text-foreground">Secure Checkout</h2>
                <p className="text-xs text-muted-foreground">Mock Payment Simulator</p>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Order Summary */}
              <div className="bg-neutral-50 dark:bg-zinc-900/50 p-4 rounded-2xl border border-border/60">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-semibold text-foreground">{selectedPlan.name}</span>
                  <span className="font-bold text-foreground">${price}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Billing cycle: {billing === 'monthly' ? 'Monthly' : 'Annual'}</span>
                  <span>Due today</span>
                </div>
              </div>

              {error && (
                <div className="flex gap-3 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300 p-4 rounded-xl text-sm border border-red-200 dark:border-red-900/50">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Form Input fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Cardholder Name</label>
                  <input
                    type="text"
                    placeholder="Jane Doe"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border border-border dark:bg-zinc-900 bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Card Number</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="4242 4242 4242 4242"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-border dark:bg-zinc-900 bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                    />
                    <CreditCard className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Expires</label>
                    <input
                      type="text"
                      placeholder="MM/YY"
                      value={cardExpiry}
                      onChange={(e) => setCardExpiry(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-border dark:bg-zinc-900 bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">CVC</label>
                    <input
                      type="text"
                      placeholder="123"
                      value={cardCvc}
                      onChange={(e) => setCardCvc(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-border dark:bg-zinc-900 bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <button
                  onClick={() => handlePayment(true)}
                  disabled={loading}
                  className="w-full py-3 px-4 rounded-xl font-semibold bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20 transition active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>Simulate Success</span>
                  )}
                </button>

                <button
                  onClick={() => handlePayment(false)}
                  disabled={loading}
                  className="w-full py-3 px-4 rounded-xl font-semibold border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition active:scale-[0.98] disabled:opacity-50"
                >
                  Simulate Card Declined
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
